import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Keyboard, MapPin, ScanLine } from 'lucide-react';
import PdaPage from '../components/PdaPage';
import {
  getPreInboundDetail,
  getPreInboundScanContext,
  PreInboundItemSummary,
  PreInboundScanContext,
  scanAddPreInboundItem,
} from '../services/api';
import { usePersistentFocus } from '../hooks/usePersistentFocus';
import { navigateTo } from '../utils/navigation';
import {
  detectScanCodeKind,
  extractPreInboundId,
  normalizeScannedCode,
} from '../utils/pdaCodes';
import { getPdaErrorMessage } from '../utils/pdaErrors';
import { notifyError, notifyInfo, notifySuccess } from '../utils/pdaFeedback';
import { formatWeight } from '../utils/formatters';
import { getStoredUser } from '../utils/session';

const AUTO_SCAN_DELAY_MS = 500;
const COMPLETED_STATUSES = new Set(['done', 'cancelled']);

type Step = 'order' | 'pickItem' | 'loc' | 'details';

function toFiniteNumber(value?: number | string | null) {
  if (value == null || value === '') {
    return undefined;
  }

  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : undefined;
}

function formatDraftDecimal(value?: number) {
  if (!Number.isFinite(value) || !value || value <= 0) {
    return '';
  }

  return value.toFixed(3).replace(/\.?0+$/, '');
}

function getDefaultDraftValues(remainingPieces: number, totalWeight: number, addedWeight: number, remainingWeight?: number) {
  if (remainingPieces <= 0) {
    return {
      length: '',
      weight: '',
    };
  }

  const normalizedRemainingWeight = toFiniteNumber(remainingWeight)
    ?? Math.max(0, totalWeight - addedWeight);

  return {
    length: '',
    weight: formatDraftDecimal(normalizedRemainingWeight / remainingPieces),
  };
}

function getContextDraftValues(scanContext: PreInboundScanContext | null) {
  if (!scanContext) {
    return {
      length: '',
      weight: '',
    };
  }

  return getDefaultDraftValues(
    scanContext.remaining_pieces,
    scanContext.total_weight,
    scanContext.added_weight,
    scanContext.remaining_weight,
  );
}

function getItemDraftValues(item: PreInboundItemSummary | null) {
  if (!item) {
    return {
      length: '',
      weight: '',
    };
  }

  return getDefaultDraftValues(
    item.remaining_pieces,
    item.total_weight,
    item.added_weight,
  );
}

function formatPreInboundStatus(status?: string) {
  switch (status) {
    case 'pending':
      return '待入库';
    case 'partial':
      return '部分入库';
    case 'done':
      return '已完成';
    case 'cancelled':
      return '已取消';
    default:
      return status || '--';
  }
}

function ScanPreInbound() {
  const inputRef = useRef<HTMLInputElement>(null);
  const scanTimerRef = useRef<number>(0);
  const [scanValue, setScanValue] = useState('');
  const [context, setContext] = useState<PreInboundScanContext | null>(null);
  const [selectedItem, setSelectedItem] = useState<PreInboundItemSummary | null>(null);
  const [pickerHighlightedItemId, setPickerHighlightedItemId] = useState<number | null>(null);
  const [locCode, setLocCode] = useState('');
  const [draftWeight, setDraftWeight] = useState('');
  const [draftLength, setDraftLength] = useState('');
  const [draftInspectionNo, setDraftInspectionNo] = useState('');
  const [draftNote, setDraftNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const operator = getStoredUser();
  const operatorDisplay = operator?.full_name || operator?.username || operator?.emp_no || '员工';
  const orderLocked = context ? COMPLETED_STATUSES.has(context.status) : false;
  const validItems = (context?.items || []).filter(
    (item) => item.remaining_pieces > 0 && !COMPLETED_STATUSES.has(item.status),
  );
  const allSkuItems = context?.items || [];
  const visualSelectedItemId = selectedItem?.id ?? pickerHighlightedItemId;
  const hasMultipleSkuItems = (context?.items?.length || 0) > 1;
  const needsItemPick = !selectedItem && hasMultipleSkuItems && validItems.length > 0;
  const remaining = selectedItem
    ? selectedItem.remaining_pieces
    : context?.remaining_pieces ?? 0;
  const allDone = Boolean(context) && (context?.remaining_pieces ?? 0) <= 0;
  const progressAddedPieces = selectedItem?.added_pieces ?? context?.added_pieces;
  const progressTotalPieces = selectedItem?.total_pieces ?? context?.total_pieces;
  const progressAddedWeight = selectedItem?.added_weight ?? context?.added_weight;
  const progressTotalWeight = selectedItem?.total_weight ?? context?.total_weight;
  const step: Step = !context
    ? 'order'
    : needsItemPick
      ? 'pickItem'
      : !locCode
        ? 'loc'
        : 'details';
  const nextPieceNo = selectedItem
    ? selectedItem.added_pieces + 1
    : (context?.added_pieces || 0) + 1;
  const scanPlaceholder = step === 'loc'
    ? '扫描输入 LOC:...'
    : '扫描输入 preinbound:N 或 PIB:N...';
  const progressSteps = [
    {
      complete: Boolean(context),
      current: step === 'order',
      icon: ScanLine,
      label: '预入库单',
    },
    {
      complete: Boolean(selectedItem) || (context ? validItems.length <= 1 : false),
      current: step === 'pickItem',
      icon: CheckCircle2,
      label: 'SKU',
    },
    {
      complete: Boolean(locCode),
      current: step === 'loc',
      icon: MapPin,
      label: '库位',
    },
    {
      complete: allDone,
      current: step === 'details' && !allDone,
      icon: CheckCircle2,
      label: '逐支录入',
    },
  ];

  usePersistentFocus(
    inputRef,
    Boolean(operator),
    `${step}:${context?.id || ''}:${selectedItem?.id || ''}:${locCode}`,
  );

  useEffect(
    () => () => {
      window.clearTimeout(scanTimerRef.current);
    },
    [],
  );

  if (!operator) {
    navigateTo('/login', true);
    return null;
  }

  function clearScanInput() {
    window.clearTimeout(scanTimerRef.current);
    setScanValue('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  function clearDraft() {
    setDraftWeight('');
    setDraftLength('');
    setDraftNote('');
  }

  function applyDefaultDraft(scanContext: PreInboundScanContext | null, item?: PreInboundItemSummary | null) {
    const defaults = item ? getItemDraftValues(item) : getContextDraftValues(scanContext);
    setDraftWeight(defaults.weight);
    setDraftLength(defaults.length);
    setDraftNote('');
  }

  function focusDraftInput(id: string) {
    window.setTimeout(() => {
      const input = document.getElementById(id) as HTMLInputElement | null;
      input?.focus();
      input?.select();
    }, 80);
  }

  function resetFlow() {
    clearScanInput();
    clearDraft();
    setContext(null);
    setSelectedItem(null);
    setPickerHighlightedItemId(null);
    setLocCode('');
    setDraftInspectionNo('');
    setLoading(false);
    setSubmitting(false);
  }

  function handleSelectItem(item: PreInboundItemSummary) {
    setSelectedItem(item);
    setPickerHighlightedItemId(item.id);
    applyDefaultDraft(context, item);
    notifySuccess(`已选 ${item.sku} · 剩余 ${item.remaining_pieces} 支`);
  }

  function handleResetItem() {
    if (draftWeight || draftLength || locCode) {
      if (!window.confirm('当前录入或库位会丢失,继续换 SKU?')) {
        return;
      }
    }
    setPickerHighlightedItemId(selectedItem?.id ?? pickerHighlightedItemId);
    setSelectedItem(null);
    setLocCode('');
    clearDraft();
    clearScanInput();
  }

  function handleOpenItemPicker() {
    if (!context || !hasMultipleSkuItems || validItems.length === 0) {
      return;
    }
    if (selectedItem || locCode || draftWeight || draftLength || draftNote) {
      handleResetItem();
      return;
    }
    setSelectedItem(null);
    clearScanInput();
  }

  function handleRescanLoc() {
    if (draftWeight || draftLength) {
      if (!window.confirm('当前录入还没提交,换库位会丢失,继续?')) {
        return;
      }
    }
    clearDraft();
    setLocCode('');
    clearScanInput();
  }

  async function loadOrder(id: string) {
    setLoading(true);
    try {
      const [response, detailResponse] = await Promise.all([
        getPreInboundScanContext(id),
        getPreInboundDetail(id).catch(() => null),
      ]);
      const data = response.data;
      const inspectionBatchNo = detailResponse?.data?.inspection_batch_no || data.inspection_batch_no || '';
      const nextContext = {
        ...data,
        inspection_batch_no: inspectionBatchNo,
      };
      setContext(nextContext);
      setSelectedItem(null);
      setPickerHighlightedItemId(null);
      setLocCode(data.last_loc_code || '');
      setDraftInspectionNo(inspectionBatchNo);

      const itemsAvailable = (data.items || []).filter(
        (item) => item.remaining_pieces > 0 && !COMPLETED_STATUSES.has(item.status),
      );
      if (itemsAvailable.length === 1) {
        setSelectedItem(itemsAvailable[0]);
        setPickerHighlightedItemId(itemsAvailable[0].id);
        applyDefaultDraft(nextContext, itemsAvailable[0]);
      } else {
        applyDefaultDraft(nextContext);
      }

      const skuCount = data.items?.length || 0;
      const sub = data.last_loc_code ? `,默认库位 ${data.last_loc_code}` : '';
      const skuTip = skuCount > 1 ? ` · ${skuCount} 个 SKU` : '';
      notifySuccess(
        `预入库单 #${data.id} · 还剩 ${data.remaining_pieces} 支${skuTip}${sub}`,
      );
    } catch (error) {
      notifyError(getPdaErrorMessage(error, '获取预入库单失败'));
    } finally {
      setLoading(false);
      clearScanInput();
    }
  }

  async function handleScan(rawCode?: string) {
    window.clearTimeout(scanTimerRef.current);
    const code = normalizeScannedCode(rawCode ?? inputRef.current?.value ?? scanValue);
    const kind = detectScanCodeKind(code);

    if (!code || loading || submitting) {
      return;
    }

    if (step === 'order') {
      if (kind === 'preInboundOrder') {
        const id = extractPreInboundId(code);
        if (!id) {
          notifyError('预入库单 QR 格式异常');
          clearScanInput();
          return;
        }
        await loadOrder(id);
        return;
      }
      notifyError('请先扫预入库单 QR (preinbound:N 或 PIB:N)');
      clearScanInput();
      return;
    }

    if (step === 'pickItem') {
      if (kind === 'preInboundOrder') {
        const id = extractPreInboundId(code);
        if (id) {
          await loadOrder(id);
          return;
        }
      }
      notifyError('请先选择要录入的 SKU');
      clearScanInput();
      return;
    }

    if (step === 'loc') {
      if (kind === 'location') {
        setLocCode(code);
        applyDefaultDraft(context, selectedItem);
        clearScanInput();
        notifySuccess(`库位 ${code}`);
        return;
      }
      if (kind === 'preInboundOrder') {
        const id = extractPreInboundId(code);
        if (id) {
          await loadOrder(id);
          return;
        }
      }
      notifyError('请扫目标库位码 (LOC:)');
      clearScanInput();
      return;
    }

    if (step === 'details') {
      if (kind === 'location') {
        setLocCode(code);
        notifySuccess(`库位 ${code}`);
        clearScanInput();
        return;
      }
      if (kind === 'preInboundOrder') {
        const id = extractPreInboundId(code);
        if (id) {
          if ((draftWeight || draftLength || draftNote) && !window.confirm('当前录入还没提交,切换预入库单会丢失,继续?')) {
            clearScanInput();
            return;
          }
          await loadOrder(id);
          return;
        }
      }
      notifyError('当前可扫预入库单 QR 或库位码');
      clearScanInput();
    }
  }

  function scheduleScan(rawCode: string) {
    if (loading || submitting) {
      return;
    }
    setScanValue(rawCode);
    window.clearTimeout(scanTimerRef.current);
    scanTimerRef.current = window.setTimeout(() => {
      void handleScan(inputRef.current?.value ?? rawCode);
    }, AUTO_SCAN_DELAY_MS);
  }

  async function handleSubmitOne() {
    if (!context || !locCode) {
      notifyError('先扫库位');
      return;
    }
    if (orderLocked) {
      notifyError('预入库单已锁定,不能继续录入');
      return;
    }
    if (validItems.length > 1 && !selectedItem) {
      notifyError('请先选择要录入的 SKU');
      return;
    }

    const normalizedWeight = draftWeight.trim();
    const normalizedLength = draftLength.trim();
    const normalizedInspectionNo = draftInspectionNo.trim();

    if (!normalizedWeight) {
      notifyError('请填写重量');
      focusDraftInput('pre-inbound-weight');
      return;
    }
    if (!normalizedLength) {
      notifyError('请填写长度');
      focusDraftInput('pre-inbound-length');
      return;
    }
    if (!normalizedInspectionNo) {
      notifyError('请填写检验单号');
      focusDraftInput('pre-inbound-inspection');
      return;
    }

    const parsedWeight = Number(normalizedWeight);
    const parsedLength = Number(normalizedLength);

    if (!Number.isFinite(parsedWeight) || parsedWeight <= 0) {
      notifyError('重量必须大于 0');
      focusDraftInput('pre-inbound-weight');
      return;
    }
    if (!Number.isFinite(parsedLength) || parsedLength <= 0) {
      notifyError('长度必须大于 0');
      focusDraftInput('pre-inbound-length');
      return;
    }

    setSubmitting(true);
    try {
      const response = await scanAddPreInboundItem(context.id, {
        pre_inbound_item_id: selectedItem?.id,
        inbound_length: parsedLength,
        inbound_weight: parsedWeight,
        inspection_no: normalizedInspectionNo,
        loc_code: locCode,
        note: draftNote.trim() || undefined,
      });
      const result = response.data;
      notifySuccess(`已入库 ${result.bid} · 累计 ${result.added_pieces}/${context.total_pieces}`);

      let nextSelectedItem = selectedItem;
      let nextItems = context.items;
      if (selectedItem) {
        const newItemAddedPieces = selectedItem.added_pieces + 1;
        const newItemAddedWeight = selectedItem.added_weight + parsedWeight;
        const newItemRemaining = Math.max(0, selectedItem.remaining_pieces - 1);
        nextSelectedItem = {
          ...selectedItem,
          added_pieces: newItemAddedPieces,
          added_weight: newItemAddedWeight,
          remaining_pieces: newItemRemaining,
          status: newItemRemaining === 0 ? 'done' : 'partial',
        };
        nextItems = context.items?.map((item) => (
          item.id === selectedItem.id ? nextSelectedItem as PreInboundItemSummary : item
        ));
      }
      const nextContext: PreInboundScanContext = {
        ...context,
        added_pieces: result.added_pieces,
        added_weight: result.added_weight,
        inbound_batch_no: result.batch_no || context.inbound_batch_no,
        items: nextItems,
        remaining_pieces: result.remaining_pieces,
        remaining_weight: Math.max(0, context.total_weight - result.added_weight),
        status: result.new_status,
      };
      setContext(nextContext);
      const nextValidItems = (nextItems || []).filter(
        (item) => item.remaining_pieces > 0 && !COMPLETED_STATUSES.has(item.status),
      );
      if (nextSelectedItem && nextSelectedItem.remaining_pieces <= 0 && result.remaining_pieces > 0 && nextValidItems.length > 0) {
        setPickerHighlightedItemId(nextSelectedItem.id);
        setSelectedItem(null);
        applyDefaultDraft(nextContext);
      } else {
        setSelectedItem(nextSelectedItem);
        setPickerHighlightedItemId(nextSelectedItem?.id ?? null);
        applyDefaultDraft(nextContext, nextSelectedItem);
      }

      if (result.new_status === 'done' || result.remaining_pieces <= 0) {
        notifyInfo('全部入库完成,可返回首页');
      } else {
        window.setTimeout(() => {
          const next = document.getElementById('pre-inbound-weight') as HTMLInputElement | null;
          next?.focus();
          next?.select();
        }, 80);
      }
    } catch (error) {
      notifyError(getPdaErrorMessage(error, '入库失败'));
    } finally {
      setSubmitting(false);
    }
  }

  function renderOrderSubtitle() {
    if (!context) {
      return '扫码后显示 SKU';
    }
    if (selectedItem) {
      return `${selectedItem.sku || '--'} · ${formatPreInboundStatus(selectedItem.status)}`;
    }
    if ((context.items?.length || 0) > 1) {
      return `${context.items?.length || 0} 个 SKU · ${formatPreInboundStatus(context.status)}`;
    }
    return `${context.sku || '--'} · ${formatPreInboundStatus(context.status)}`;
  }

  return (
    <PdaPage
      actionLabel="重置"
      onAction={resetFlow}
      onBack={() => navigateTo('/home')}
      pageClassName="pre-inbound-page"
      subtitle="预入库单 QR → 库位 → 逐支录入"
      title="原材料入库"
      footer={
        step === 'details' && !allDone ? (
          <div className="pre-footer-actions">
            {hasMultipleSkuItems && validItems.length > 0 ? (
              <button className="secondary-btn" disabled={submitting} onClick={handleResetItem} type="button">
                换 SKU
              </button>
            ) : null}
            <button className="secondary-btn" disabled={submitting} onClick={handleRescanLoc} type="button">
              换库位
            </button>
            <button
              className="primary-btn"
              disabled={submitting || orderLocked}
              onClick={() => void handleSubmitOne()}
              type="button"
            >
              {submitting ? '提交中...' : '提交本支'}
            </button>
          </div>
        ) : null
      }
    >
      <section className="pre-progress-card">
        <div className="pre-progress-title">当前进度</div>
        <div className="pre-step-track">
          {progressSteps.map((item) => {
            const StepIcon = item.icon;
            const className = [
              'pre-step',
              item.complete ? 'pre-step--complete' : '',
              item.current ? 'pre-step--current' : '',
              item.complete ? 'pre-step--line-complete' : '',
            ].filter(Boolean).join(' ');

            return (
              <div className={className} key={item.label}>
                <div className="pre-step__line" />
                <div className="pre-step__icon">
                  <StepIcon aria-hidden="true" size={15} strokeWidth={2.4} />
                </div>
                <div className="pre-step__label">{item.label}</div>
              </div>
            );
          })}
        </div>
        <div className="pre-progress-count">
          <div className="pre-progress-count__label">已入库</div>
          <div className="pre-progress-count__value">
            {progressAddedPieces != null && progressTotalPieces != null
              ? `${progressAddedPieces}/${progressTotalPieces}`
              : '--'} 支
          </div>
          <div className="pre-progress-count__sub">
            {progressAddedWeight != null && progressTotalWeight != null
              ? `${formatWeight(progressAddedWeight)}/${formatWeight(progressTotalWeight)}kg`
              : '--'}
          </div>
        </div>
      </section>

      <section className="pre-info-card">
        <div className="pre-info-grid">
          <div className="pre-info-cell">
            <div className="pre-label">操作人</div>
            <div className="pre-value">
              {operatorDisplay}{operator.emp_no ? ` · ${operator.emp_no}` : ''}
            </div>
          </div>
          <div className="pre-info-cell">
            <div className="pre-label">当前库位</div>
            <div className="pre-value">{locCode || (context ? '待扫描' : '先扫预入库单')}</div>
          </div>
        </div>
        <button
          className="pre-order-card"
          disabled={!context || !hasMultipleSkuItems || validItems.length === 0}
          onClick={handleOpenItemPicker}
          type="button"
        >
          <div>
            <div className="pre-label">预入库单</div>
            <div className="pre-order-title">{context ? context.inbound_batch_no || `#${context.id}` : '等待扫码'}</div>
            <div className="pre-order-sub">{renderOrderSubtitle()}</div>
          </div>
          <span className="pre-chevron">›</span>
        </button>
      </section>

      {step !== 'pickItem' ? (
        <section className="pre-scan-card" key={`scan-${step}-${context?.id || ''}`}>
          <div className="panel-title">扫码录入</div>
          <div className="pre-scan-box">
            <ScanLine className="pre-scan-mark" aria-hidden="true" size={18} strokeWidth={2.2} />
            <input
              autoCapitalize="off"
              autoComplete="off"
              autoCorrect="off"
              autoFocus
              className="pre-scan-input"
              disabled={loading || submitting}
              id="scan-pre-inbound-code"
              inputMode="none"
              onInput={(event) => scheduleScan(event.currentTarget.value)}
              onKeyUp={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleScan(event.currentTarget.value);
                }
              }}
              placeholder={scanPlaceholder}
              ref={inputRef}
              spellCheck={false}
            />
            <Keyboard className="pre-keyboard-mark" aria-hidden="true" size={18} strokeWidth={2.2} />
          </div>
        </section>
      ) : null}

      {step === 'pickItem' ? (
        <section className="pre-entry-card" key={`pick-item-${context?.id || ''}`}>
          <div className="pre-pick-head">
            <div className="pre-entry-title">请选择要入库的 SKU</div>
          </div>
          <div className="pre-pick-list">
            {allSkuItems.map((item) => {
              const selectable = item.remaining_pieces > 0 && !COMPLETED_STATUSES.has(item.status);
              const checked = item.id === visualSelectedItemId;
              const className = [
                'pre-pick-item',
                checked ? 'pre-pick-item--active' : '',
                selectable ? '' : 'pre-pick-item--disabled',
              ].filter(Boolean).join(' ');

              return (
                <button
                  className={className}
                  disabled={!selectable}
                  key={item.id}
                  onClick={() => handleSelectItem(item)}
                  type="button"
                >
                  <span aria-hidden="true" className="pre-pick-item__radio" />
                  <span className="pre-pick-item__body">
                    <span className="pre-pick-item__title">{item.sku || `行 ${item.id}`}</span>
                    <span className="pre-pick-item__meta">
                      {item.contract_spec || '无规格'} · 炉号 {item.smelt_heat_no || '--'} · {item.steel_mill_grade_raw || '--'}
                    </span>
                    <span className="pre-pick-item__sub">
                      {selectable
                        ? `剩余 ${item.remaining_pieces}/${item.total_pieces} 支 · ${formatPreInboundStatus(item.status)}`
                        : `已完成 ${item.added_pieces}/${item.total_pieces} 支 · ${formatPreInboundStatus(item.status)}`}
                    </span>
                  </span>
                  <span className={selectable ? 'pre-pick-item__badge' : 'pre-pick-item__badge pre-pick-item__badge--done'}>
                    {selectable ? '可入库' : '已完成'}
                  </span>
                </button>
              );
            })}
          </div>
          {validItems.length > 0 ? (
            <div className="pre-pick-tip">
              <span className="pre-pick-tip__icon">i</span>
              <span>选择后即可继续扫码入库操作</span>
            </div>
          ) : null}
          {validItems.length === 0 ? (
            <div className="empty-state">本批次所有 SKU 都已入完或被锁定。</div>
          ) : null}
        </section>
      ) : step === 'details' ? (
        <section className="pre-entry-card" key={`details-${context?.id || ''}-${context?.added_pieces || 0}`}>
          {allDone ? (
            <div className="button-row">
              <button className="primary-btn" onClick={() => navigateTo('/home')} type="button">
                回首页
              </button>
              <button className="secondary-btn" onClick={resetFlow} type="button">
                扫下一单
              </button>
            </div>
          ) : (
            <>
              <div className="pre-entry-head">
                <div>
                  <div className="pre-entry-title">录入第 {nextPieceNo} 支</div>
                  <div className="pre-entry-sub">
                    还剩 {remaining} 支 · 累计 {formatWeight(context?.added_weight)}kg
                    {selectedItem ? ` · ${selectedItem.sku}` : ''}
                    {context?.inbound_batch_no ? ` · 批次 ${context.inbound_batch_no}` : ''}
                  </div>
                </div>
                <span className="pre-chevron">›</span>
              </div>

              <div className="pre-field-grid">
                <label className="pre-field-cell" htmlFor="pre-inbound-weight">
                  <span>重量(kg)</span>
                  <input
                    disabled={submitting || orderLocked}
                    id="pre-inbound-weight"
                    inputMode="decimal"
                    onChange={(event) => setDraftWeight(event.target.value)}
                    placeholder="105.5"
                    value={draftWeight}
                  />
                </label>
                <label className="pre-field-cell" htmlFor="pre-inbound-length">
                  <span>长度(mm)</span>
                  <input
                    disabled={submitting || orderLocked}
                    id="pre-inbound-length"
                    inputMode="decimal"
                    onChange={(event) => setDraftLength(event.target.value)}
                    placeholder="6000"
                    value={draftLength}
                  />
                </label>
                <label className="pre-field-cell" htmlFor="pre-inbound-inspection">
                  <span>检验单号</span>
                  <input
                    disabled={submitting || orderLocked}
                    id="pre-inbound-inspection"
                    onChange={(event) => setDraftInspectionNo(event.target.value)}
                    placeholder="不填继承"
                    value={draftInspectionNo}
                  />
                </label>
              </div>

              <label className="pre-note-field" htmlFor="pre-inbound-note">
                <span>备注(可选)</span>
                <input
                  disabled={submitting || orderLocked}
                  id="pre-inbound-note"
                  onChange={(event) => setDraftNote(event.target.value)}
                  placeholder="现场补充说明 (可选)"
                  value={draftNote}
                />
                <span className="pre-note-edit" aria-hidden="true" />
              </label>
            </>
          )}
        </section>
      ) : (
        <section className="panel empty-state">
          {step === 'order' ? '先扫描预入库单 QR。' : '扫描库位后开始录入每支重量、长度和检验单号。'}
        </section>
      )}
    </PdaPage>
  );
}

export default ScanPreInbound;
